import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronUp, ChevronDown, ChevronDown as ExpandIcon, Eye, EyeOff, Trash2, PlusCircle, ExternalLink, X, ImageIcon } from 'lucide-react'
import { GalleryModal } from '@/components/gallery/gallery-modal'
import MediaPicker from '@/components/media/media-picker'
import { useAdjustAssetUsage } from '@/hooks/use-assets'
import { useCatalogComponents } from '@/hooks/use-catalog'
import { useAuthStore } from '@/stores/auth-store'
import type { CatalogComponent } from '@/types/components'
import type { BlockConfig } from '@/hooks/use-homepage'

// ─── BlockRow ────────────────────────────────────────────────────────────────

function BlockRow({
  block,
  index,
  total,
  onChange,
  onMove,
  onDelete,
  onPreview,
  catalogMap,
}: {
  block: BlockConfig
  index: number
  total: number
  onChange: (b: BlockConfig) => void
  onMove: (from: number, to: number) => void
  onDelete: (index: number) => void
  onPreview: () => void
  catalogMap: Record<string, CatalogComponent>
}) {
  const [expanded, setExpanded] = useState(false)
  const [mediaPickerFieldKey, setMediaPickerFieldKey] = useState<string | null>(null)
  const adjustUsage = useAdjustAssetUsage()
  const def: CatalogComponent | undefined = catalogMap[block.component]
  const label = def?.label ?? block.component
  const category = def?.category ?? ''
  const fields = def?.settings_schema ?? []

  function setField(key: string, value: unknown) {
    onChange({ ...block, settings: { ...block.settings, [key]: value } })
  }

  return (
    <div className={`rounded-xl border bg-card overflow-hidden ${!block.enabled ? 'opacity-60' : ''}`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3">
        {/* Reorder */}
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={() => onMove(index, index - 1)}
            disabled={index === 0}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove(index, index + 1)}
            disabled={index === total - 1}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Label + category */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">{label}</span>
          {category && (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              {category}
            </span>
          )}
        </div>

        {/* Toggle visible */}
        <button
          type="button"
          onClick={() => onChange({ ...block, enabled: !block.enabled })}
          className="text-muted-foreground hover:text-foreground"
          title={block.enabled ? 'Sembunyikan' : 'Tampilkan'}
        >
          {block.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>

        {/* Preview */}
        <button
          type="button"
          onClick={onPreview}
          className="text-muted-foreground hover:text-foreground"
          title="Preview komponen"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>

        {/* Delete */}
        <button
          type="button"
          onClick={() => onDelete(index)}
          className="text-muted-foreground hover:text-destructive"
          title="Hapus block"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>

        {/* Expand settings */}
        {fields.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ExpandIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      <MediaPicker
        open={mediaPickerFieldKey !== null}
        onClose={() => setMediaPickerFieldKey(null)}
        onSelect={(assetId, url) => {
          if (mediaPickerFieldKey) {
            const assetIdKey = `${mediaPickerFieldKey}_asset_id`
            const oldAssetId = block.settings[assetIdKey] as string | undefined
            if (oldAssetId) adjustUsage.mutate({ assetId: oldAssetId, delta: -1 })
            onChange({
              ...block,
              settings: { ...block.settings, [mediaPickerFieldKey]: url, [assetIdKey]: assetId },
            })
            adjustUsage.mutate({ assetId, delta: 1 })
          }
          setMediaPickerFieldKey(null)
        }}
      />

      {/* Settings panel */}
      {expanded && fields.length > 0 && (
        <div className="border-t border-border px-4 py-4 bg-background/50 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pengaturan</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((field) => (
              <div key={field.key} className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">{field.label}</label>

                {field.type === 'boolean' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(block.settings[field.key] as boolean) ?? false}
                      onChange={(e) => setField(field.key, e.target.checked)}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <span className="text-xs text-muted-foreground">
                      {(block.settings[field.key] as boolean) ? 'Ya' : 'Tidak'}
                    </span>
                  </div>
                ) : field.type === 'select' ? (
                  <select
                    value={(block.settings[field.key] as string) ?? ''}
                    onChange={(e) => setField(field.key, e.target.value || undefined)}
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Default</option>
                    {field.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={(block.settings[field.key] as string) ?? ''}
                    onChange={(e) => setField(field.key, e.target.value || undefined)}
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                    placeholder={field.placeholder}
                  />
                ) : field.type === 'image' ? (
                  <div className="space-y-1.5">
                    {block.settings[field.key] ? (
                      <div className="relative w-full h-20 rounded-md overflow-hidden border border-border group">
                        <img
                          src={block.settings[field.key] as string}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const oldAssetId = block.settings[`${field.key}_asset_id`] as string | undefined
                            if (oldAssetId) adjustUsage.mutate({ assetId: oldAssetId, delta: -1 })
                            onChange({
                              ...block,
                              settings: { ...block.settings, [field.key]: undefined, [`${field.key}_asset_id`]: undefined },
                            })
                          }}
                          className="absolute top-1 right-1 rounded bg-black/60 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Hapus gambar"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setMediaPickerFieldKey(field.key)}
                      className="flex items-center gap-1.5 w-full rounded-md border border-dashed border-input bg-background px-2.5 py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      <ImageIcon className="h-3 w-3" />
                      {block.settings[field.key] ? 'Ganti gambar...' : 'Pilih dari library...'}
                    </button>
                  </div>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={(block.settings[field.key] as string | number) ?? ''}
                    onChange={(e) =>
                      setField(
                        field.key,
                        field.type === 'number'
                          ? (e.target.value ? Number(e.target.value) : undefined)
                          : (e.target.value || undefined)
                      )
                    }
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={field.placeholder ?? 'Default'}
                    step={field.type === 'number' ? '0.1' : undefined}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── BlockEditor ─────────────────────────────────────────────────────────────

interface BlockEditorProps {
  /** Title shown at top of editor (e.g. "Homepage", "Members") */
  pageTitle: string
  /** Description shown below title */
  pageDescription?: string
  /** Initial blocks loaded from DB */
  initialBlocks: BlockConfig[]
  /** Called with the final block array when user clicks Save */
  onSave: (blocks: BlockConfig[]) => void
  isSaving: boolean
  saveError?: Error | null
  saved?: boolean
  /** When navigating from Gallery, auto-add component passed via router state */
  listenToRouterState?: boolean
  /**
   * Scope filter for components shown in the gallery modal.
   * - undefined/null: show only universal components (page_scope === null)
   * - 'member-detail': show universal + member-detail scoped components
   */
  pageScope?: string
}

export function BlockEditor({
  pageTitle,
  pageDescription,
  initialBlocks,
  onSave,
  isSaving,
  saveError,
  saved,
  listenToRouterState = false,
  pageScope,
}: BlockEditorProps) {
  const location = useLocation()
  const tenantSlug = useAuthStore((s) => s.currentTenant?.slug)
  const { data: catalogComponents = [], isLoading: catalogLoading } = useCatalogComponents()
  const [blocks, setBlocks] = useState<BlockConfig[]>(initialBlocks)
  const [galleryOpen, setGalleryOpen] = useState(false)

  // Build a lookup map: component_key → CatalogComponent
  const catalogMap = useMemo(
    () => Object.fromEntries(catalogComponents.map((c) => [c.component_key, c])),
    [catalogComponents]
  )

  // Filter catalog based on pageScope:
  // - No scope (universal page): show only components with no page_scope
  // - Specific scope: show universal + matching scope
  const scopedComponents = useMemo(() => {
    if (!pageScope) {
      return catalogComponents.filter((c) => !c.page_scope)
    }
    return catalogComponents.filter((c) => !c.page_scope || c.page_scope === pageScope)
  }, [catalogComponents, pageScope])

  // Sync when parent updates initialBlocks (e.g., after data loads)
  useEffect(() => {
    setBlocks(initialBlocks)
  }, [initialBlocks])

  // Handle navigate-from-gallery: auto-add component passed via router state
  useEffect(() => {
    if (!listenToRouterState) return
    const state = location.state as { addComponent?: string } | null
    if (!state?.addComponent) return
    const def = catalogMap[state.addComponent]
    if (!def) return // catalog belum load — tunggu re-run saat catalogMap update
    addBlock(def)
    window.history.replaceState({}, '') // clear hanya setelah block berhasil ditambahkan
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, catalogMap, listenToRouterState])

  function addBlock(def: CatalogComponent) {
    const newBlock: BlockConfig = {
      id: crypto.randomUUID(),
      component: def.component_key,
      enabled: true,
      order: blocks.length + 1,
      settings: { ...def.default_settings },
    }
    setBlocks((prev) => [...prev, newBlock])
  }

  function updateBlock(index: number, updated: BlockConfig) {
    setBlocks((prev) => prev.map((b, i) => (i === index ? updated : b)))
  }

  function deleteBlock(index: number) {
    setBlocks((prev) => prev.filter((_, i) => i !== index).map((b, i) => ({ ...b, order: i + 1 })))
  }

  function moveBlock(from: number, to: number) {
    if (to < 0 || to >= blocks.length) return
    setBlocks((prev) => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next.map((b, i) => ({ ...b, order: i + 1 }))
    })
  }

  function handleSave() {
    const normalized = blocks.map((b, i) => ({ ...b, order: i + 1 }))
    onSave(normalized)
  }

  function handlePreview(block: BlockConfig) {
    const publicBase = import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined
    const previewPath = publicBase
      ? `${publicBase}/${tenantSlug}/preview`
      : `https://${tenantSlug}.oneidol.id/preview`
    const blocksParam = encodeURIComponent(JSON.stringify([block]))
    window.open(`${previewPath}?blocks=${blocksParam}`, '_blank', 'noopener')
  }

  if (catalogLoading) return <p className="text-muted-foreground">Memuat komponen...</p>

  return (
    <>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
          {pageDescription && (
            <p className="text-muted-foreground">{pageDescription}</p>
          )}
        </div>

        <div className="space-y-3">
          {blocks.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded-xl">
              Belum ada block. Tambahkan dari galeri komponen.
            </p>
          )}
          {blocks.map((block, index) => (
            <BlockRow
              key={block.id}
              block={block}
              index={index}
              total={blocks.length}
              onChange={(updated) => updateBlock(index, updated)}
              onMove={moveBlock}
              onDelete={deleteBlock}
              onPreview={() => handlePreview(block)}
              catalogMap={catalogMap}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setGalleryOpen(true)}
            className="flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <PlusCircle className="h-4 w-4" />
            Tambah Komponen
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isSaving ? 'Menyimpan...' : 'Simpan'}
          </button>

          {saved && <p className="text-sm text-green-600">Berhasil disimpan!</p>}
          {saveError && (
            <p className="text-sm text-destructive">
              {saveError.message ?? 'Terjadi kesalahan.'}
            </p>
          )}
        </div>
      </div>

      <GalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={addBlock}
        components={scopedComponents}
        isLoading={catalogLoading}
      />
    </>
  )
}
